import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Button, Stack } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  setPlotArea,
  setStartPeriod,
  setVariable,
  setEndPeriod,
  clearDates,
  Variable,
  setSelectedPeriod,
  setAnomaly,
  setMode as setModeRedux,
  setViewMode as setViewModeRedux,
  setLastConsulted,
  MeteorologyViewMode,
  selectMFControlPanelObservedDataStartPeriod,
  selectMFControlPanelObservedDataEndPeriod,
  selectMFControlPanelObservedDataPlotArea,
  selectMFControlPanelObservedDataSelectedPeriod,
  selectMFControlPanelObservedDataVariable,
  selectMFControlPanelObservedDataAnomaly,
  selectMFControlPanelObservedDataObsImageString,
  selectMFControlPanelObservedDataClimatologyImageString,
  selectMFControlPanelObservedDataAnomalyImageString,
  selectMFControlPanelObservedDataHeatmapImageString,
  selectMFControlPanelObservedDataImageLoading,
  selectMFControlPanelObservedDataMode,
  selectMFControlPanelObservedDataViewMode,
  selectMFControlPanelObservedDataLastConsulted,
  setObsImageString,
  setClimatologyImageString,
  setAnomalyImageString,
  setHeatmapImageString,
  setImageLoading,
  selectMFControlPanelObservedDataErrorAlert,
  setErrorAlert,
} from 'redux/reducers/meteorologyFieldsObservedDataControlPanelReducer';
import {
  setHours,
  addDays,
  isBefore,
  isAfter,
  startOfDay,
  roundToNearestMinutes,
  format,
} from 'date-fns';

import { ResponseError } from 'services/api/base';
import {
  useLazyGetObservedDataImagesQuery,
  useLazyGetObservedDataHeatmapQuery,
} from 'services/api/Meteorology';
import DatePicker from 'components/DatePicker';
import Snackbar from 'components/DesignSystem/Snackbar';
import { ObservedDataPeriodMode } from 'types/Meteorology/Fields/ObservedData/getOptions';
import useObservedDataOptions from './useObservedDataOptions';

// ✅ Defaults: deixe fora do hook para não “mudar” por render
const DEFAULT_PLOT_AREA = 'BR';
const DEFAULT_VARIABLE = 'prec' as any as Variable; // força conversão
const DEFAULT_PERIOD = 'neste mes'; // exatamente como no payload
const DEFAULT_ANOMALY = true;
// Dados observados existem a partir de 01/01/2000 (sem dados anteriores no banco)
// Usar new Date(2000, 0, 1) e não new Date('2000-01-01'): a forma com string é UTC,
// o que resulta em 31/12/1999 no horário local do Brasil (-3h).
const OBSERVED_DATA_MIN_DATE = new Date(2000, 0, 1);

export default function useObservedDataControlPanel() {
  const { options, optionsLoading } = useObservedDataOptions();

  const dispatcher = useDispatch();
  const plotArea = useSelector(selectMFControlPanelObservedDataPlotArea);
  const variable = useSelector(selectMFControlPanelObservedDataVariable);
  const startDate = useSelector(selectMFControlPanelObservedDataStartPeriod);
  const endDate = useSelector(selectMFControlPanelObservedDataEndPeriod);
  const selectedPeriod = useSelector(selectMFControlPanelObservedDataSelectedPeriod);
  const anomaly = useSelector(selectMFControlPanelObservedDataAnomaly);
  const obsBase64 = useSelector(selectMFControlPanelObservedDataObsImageString);
  const climatologyBase64 = useSelector(selectMFControlPanelObservedDataClimatologyImageString);
  const anomalyBase64 = useSelector(selectMFControlPanelObservedDataAnomalyImageString);
  const heatmapBase64 = useSelector(selectMFControlPanelObservedDataHeatmapImageString);
  const imagesAreFetching = useSelector(selectMFControlPanelObservedDataImageLoading);
  const alerts = useSelector(selectMFControlPanelObservedDataErrorAlert);
  const mode = useSelector(selectMFControlPanelObservedDataMode);
  const viewMode = useSelector(selectMFControlPanelObservedDataViewMode);
  const lastConsulted = useSelector(selectMFControlPanelObservedDataLastConsulted);
  const consultedViewMode = lastConsulted?.viewMode;

  const isHeatmap = viewMode === 'heatmap';
  const isConsultedHeatmap = consultedViewMode === 'heatmap';
  const viewModeLabel = isHeatmap ? 'Heatmap' : 'Espacial';

  // Variavel correspondente ao 'variable' selecionado (objeto vindo do painel-options
  // ja transformado pelo helper). Mesma referencia usada por areParamsSelected, nextDateLabels etc.
  const currentVariableOption = useMemo(
    () => options?.variables?.find((v) => v.id === variable),
    [options?.variables, variable],
  );

  // Heatmap fica desabilitado quando a variavel atual nao declara suporte para ele.
  // Fonte da verdade: backend via `visualizacoes_suportadas` (helper transforma em
  // `supportedVisualizations`). Antes era hardcoded `=== 'prec'`.
  const isHeatmapDisabled = useMemo(
    () => !currentVariableOption?.supportedVisualizations?.includes('heatmap'),
    [currentVariableOption],
  );

  // Rotulo derivado para a casca mobile (context card + chip do painel). Nao existe
  // no desktop porque o ControlPanel la usa VariableSelector (render-prop) que ja
  // mostra o label dentro do proprio botao.
  const variableLabel = currentVariableOption?.label;

  const [queryTrigger, { data, error, isFetching }] = useLazyGetObservedDataImagesQuery();
  const [
    heatmapTrigger,
    { data: heatmapData, error: heatmapError, isFetching: heatmapIsFetching },
  ] = useLazyGetObservedDataHeatmapQuery();

  const regionObject = useMemo(
    () => options?.regions?.map((region) => ({ label: region.label, value: region.id })) || [],
    [options?.regions],
  );

  // Mesma fonte (`options.regions`), ja no shape {id,label} — usado pela casca mobile,
  // que desenha suas proprias linhas de selecao em vez do render-prop RegionSelectorOD.
  const regionOptions = useMemo(() => options?.regions ?? [], [options?.regions]);
  const plotAreaLabel = useMemo(
    () => regionOptions.find((r) => r.id === plotArea)?.label,
    [regionOptions, plotArea],
  );

  const availableVariables = useMemo(
    () => options?.variables?.map((v) => ({ id: v.id, label: v.label })) || [],
    [options?.variables],
  );

  // labels por id do periodo (id -> label).
  // Fonte da verdade: backend via `predefinido_por_visualizacao` (helper transforma em
  // `predefinedPeriodsByVisualization`). Antes filtravamos por HEATMAP_ALLOWED_PERIODS hardcoded.
  const nextDateLabels = useMemo(() => {
    if (!currentVariableOption) return {};
    const periods = isHeatmap
      ? currentVariableOption.predefinedPeriodsByVisualization.heatmap
      : currentVariableOption.predefinedPeriodsByVisualization.spatial;
    return periods.reduce((obj, item) => {
      // eslint-disable-next-line no-param-reassign
      obj[item.id] = item.label;
      return obj;
    }, {} as { [key: string]: string });
  }, [currentVariableOption, isHeatmap]);

  // Rotulo do periodo para o chip do context card mobile. Nos modos 'date'/'free' nao
  // ha label pre-definido (o usuario escolheu datas livres), entao formata o intervalo.
  const periodLabel = useMemo(() => {
    if (isHeatmap || mode === 'preset') {
      return selectedPeriod ? nextDateLabels[selectedPeriod] : undefined;
    }
    if (startDate && endDate) {
      return `${format(startDate, 'dd/MM')} – ${format(endDate, 'dd/MM')}`;
    }
    return undefined;
  }, [isHeatmap, mode, selectedPeriod, nextDateLabels, startDate, endDate]);

  const availableTabs = useMemo(
    () => options?.variables?.find((v) => v.id === variable)?.activeTabs || [],
    [options?.variables, variable],
  );

  // ✅ init 1x sem re-render
  const isInitializedRef = useRef(false);

  // ✅ Setar defaults quando options carregar (apenas na primeira vez)
  useEffect(() => {
    if (!options || isInitializedRef.current) return;

    if (!plotArea) dispatcher(setPlotArea(DEFAULT_PLOT_AREA));
    if (!variable) dispatcher(setVariable(DEFAULT_VARIABLE));
    if (!selectedPeriod) dispatcher(setSelectedPeriod(DEFAULT_PERIOD));

    // garante anomalia ligada no primeiro acesso
    if (anomaly !== DEFAULT_ANOMALY) dispatcher(setAnomaly(DEFAULT_ANOMALY));

    // garante modo preset no primeiro acesso (só se ainda não foi setado)
    if (!mode) dispatcher(setModeRedux('preset'));

    isInitializedRef.current = true;
  }, [
    options,
    plotArea,
    variable,
    selectedPeriod,
    anomaly,
    mode,
    dispatcher,
  ]);

  const setMode = (newMode: ObservedDataPeriodMode) => {
    dispatcher(setModeRedux(newMode));
  };

  const areParamsSelected = useMemo(() => {
    if (isHeatmap) {
      return Boolean(variable && selectedPeriod);
    }
    switch (mode) {
      case 'preset':
        return Boolean(plotArea && variable && selectedPeriod);
      case 'date':
        return Boolean(plotArea && variable && startDate && endDate);
      case 'free':
        return Boolean(plotArea && variable && startDate && endDate);
      default:
        return false;
    }
  }, [isHeatmap, mode, plotArea, variable, selectedPeriod, startDate, endDate]);

  const queryTriggerMethod = useMemo(
    () => () => {
      if (!areParamsSelected) return;
      dispatcher(setLastConsulted({
        viewMode,
        variable,
        selectedPeriod,
        plotArea,
        startDate,
        endDate,
      }));
      if (isHeatmap) {
        heatmapTrigger(
          {
            variable: variable as unknown as string,
            preset: selectedPeriod as string,
          },
          true,
        );
        return;
      }
      queryTrigger(
        {
          region: plotArea,
          variable,
          dataPerStation: false,
          initialTimestamp: !startDate ? undefined : Math.ceil(startDate / 1000),
          finalTimestamp: !endDate ? undefined : Math.ceil(endDate / 1000),
          preset: selectedPeriod,
          anomaly,
        },
        selectedPeriod !== undefined,
      );
      dispatcher(clearDates());
    },
    [
      isHeatmap,
      viewMode,
      heatmapTrigger,
      queryTrigger,
      dispatcher,
      plotArea,
      variable,
      startDate,
      endDate,
      selectedPeriod,
      areParamsSelected,
      anomaly,
    ],
  );

  // Estado para controlar se já disparou automaticamente
  const [didAutoLoad, setDidAutoLoad] = useState(false);

  // Disparar automaticamente o carregamento quando params estiverem prontos
  useEffect(() => {
    if (didAutoLoad) return;
    if (!areParamsSelected) return;

    queryTriggerMethod();
    setDidAutoLoad(true);
  }, [areParamsSelected, queryTriggerMethod, didAutoLoad]);

  useEffect(() => {
    const obsImage = data?.data?.obs && !error ? data?.data?.obs : '';
    const climatologyImage = data?.data?.clim && !error ? data?.data?.clim : '';
    const anomalyImage = data?.data?.anom && !error ? data?.data?.anom : '';

    dispatcher(setObsImageString(obsImage));
    dispatcher(setClimatologyImageString(climatologyImage));
    dispatcher(setAnomalyImageString(anomalyImage));
    dispatcher(setErrorAlert(error as ResponseError | undefined));
    dispatcher(setImageLoading(isFetching));
  }, [data, dispatcher, error, isFetching]);

  useEffect(() => {
    const heatmapImage = heatmapData?.data?.image && !heatmapError ? heatmapData.data.image : '';
    dispatcher(setHeatmapImageString(heatmapImage));
    dispatcher(setErrorAlert(heatmapError as ResponseError | undefined));
    dispatcher(setImageLoading(heatmapIsFetching));
  }, [heatmapData, heatmapError, heatmapIsFetching, dispatcher]);

  function SelectionButton({
    text,
    value,
    currentValue,
    modifier,
    extraClassName,
  }: {
    text: string;
    value: string;
    currentValue: any;
    modifier: any;
    extraClassName: string | undefined;
  }) {
    const activeStyle = currentValue === value ? 'btn-control-panel-active' : '';

    return (
      <Button
        variant="link"
        onClick={() => modifier(value)}
        className={`btn-control-panel ${activeStyle} ${extraClassName}`}
      >
        {text}
      </Button>
    );
  }

  const setPeriodOption = (option: string) => dispatcher(setSelectedPeriod(option));
  const setRegion = (region: string) => dispatcher(setPlotArea(region));
  const setSelectedVariable = (newVariable: Variable) => {
    dispatcher(setVariable(newVariable));
    // Se a variavel recem-selecionada nao suporta heatmap e estamos em modo heatmap,
    // volta para spatial. Antes era hardcode `=== 'prec'` no reducer; agora consulta
    // a matriz vinda do backend via `supportedVisualizations`.
    const nextOption = options?.variables?.find(
      (v) => v.id === (newVariable as unknown as string),
    );
    if (viewMode === 'heatmap' && nextOption
        && !nextOption.supportedVisualizations.includes('heatmap')) {
      dispatcher(setViewModeRedux('spatial'));
    }
  };
  const setStartTime = (date: Date | null) => dispatcher(setStartPeriod(date?.getTime()));
  const setEndTime = (date: Date | null) => dispatcher(setEndPeriod(date?.getTime()));
  const toggleAnomaly = () => dispatcher(setAnomaly(!anomaly));

  function PeriodSelectors() {
    return (
      <>
        {Object.keys(nextDateLabels).length === 0 && (
          <Snackbar className="text-center">
            Nenhum período pré-definido
            <br />
            disponível para a variável
            <br />
            selecionada
          </Snackbar>
        )}

        {/* ✅ Aqui era o bug: use o ID (key) como value, e o label só pra exibição */}
        {Object.keys(nextDateLabels).map((key: string) => (
          <SelectionButton
            text={nextDateLabels[key]}
            value={key}
            key={key}
            currentValue={selectedPeriod}
            modifier={setPeriodOption}
            extraClassName="period-pills"
          />
        ))}
      </>
    );
  }

  function RegionSelectorOD() {
    return (
      <>
        {regionObject.map(({ label, value }) => (
          <SelectionButton
            key={label}
            text={label}
            value={value}
            currentValue={plotArea}
            modifier={setRegion}
            extraClassName=""
          />
        ))}
      </>
    );
  }

  function VariableSelector() {
    return (
      <>
        {availableVariables.map((v) => (
          <SelectionButton
            key={v.label}
            text={v.label}
            value={v.id}
            currentValue={variable}
            modifier={setSelectedVariable}
            extraClassName=""
          />
        ))}
      </>
    );
  }

  function DateSelector() {
    const initialStart = startDate ? addDays(new Date(startDate), 1) : null;
    const initialEnd = endDate ? new Date(endDate) : null;
    const [selectedStart, setSelectedStart] = useState<Date | null>(initialStart);
    const [selectedEnd, setSelectedEnd] = useState<Date | null>(initialEnd);

    const startDateFilter = (time: Date) => !isAfter(time, selectedEnd || new Date());
    const endDateFilter = (time: Date) => (
      (!startDate || !isBefore(time, startDate)) && !isAfter(time, new Date())
    );

    useEffect(() => {
      if (selectedStart) {
        const baseDay = addDays(selectedStart, -1);
        const newStartDate = setHours(baseDay, 12);
        setStartTime(newStartDate);
      }
    }, [selectedStart]);

    useEffect(() => {
      if (selectedEnd) {
        const newEndDate = setHours(selectedEnd, 11);
        setEndTime(newEndDate);
      }
    }, [selectedEnd]);

    return (
      <Stack gap={2}>
        <DatePicker
          className="period-selector"
          containerClass="ds-scroll-datepicker"
          dropdownMode="scroll"
          scrollableYearDropdown
          yearDropdownItemNumber={30}
          selected={selectedStart}
          onChange={(d) => setSelectedStart(d)}
          placeholderText="Período Inicial"
          filterDate={startDateFilter}
          minDate={OBSERVED_DATA_MIN_DATE}
          maxDate={new Date()}
        />
        <DatePicker
          className="period-selector"
          containerClass="ds-scroll-datepicker"
          dropdownMode="scroll"
          scrollableYearDropdown
          yearDropdownItemNumber={30}
          selected={selectedEnd}
          onChange={(d) => setSelectedEnd(d)}
          placeholderText="Período Final"
          filterDate={endDateFilter}
          minDate={OBSERVED_DATA_MIN_DATE}
          maxDate={new Date()}
        />
      </Stack>
    );
  }

  function FreePeriodSetter() {
    const presentDate = roundToNearestMinutes(new Date(), {
      nearestTo: 30,
      roundingMethod: 'floor',
    });
    const selectedStart = startDate ? new Date(startDate) : null;
    const selectedEnd = endDate ? new Date(endDate) : null;
    const maxDate = selectedEnd || presentDate;

    const startDateFilter = (date: Date) => !isAfter(date, maxDate);
    const endDateFilter = (date: Date) => (
      (!startDate || !isBefore(date, startOfDay(startDate))) && !isAfter(date, presentDate)
    );
    const endDateTimeFilter = (date: Date) => (
      (!startDate || !isBefore(date, startDate)) && !isAfter(date, presentDate)
    );

    const handleStartDate = (date: Date | null) => {
      if (date && isAfter(date, maxDate)) {
        setStartTime(maxDate);
      } else {
        setStartTime(date);
      }
    };

    const handleEndDate = (date: Date | null) => {
      if (date && selectedStart && isBefore(date, selectedStart)) {
        setEndTime(selectedStart);
      } else {
        setEndTime(date);
      }
    };

    return (
      <Stack gap={2}>
        <DatePicker
          className="period-selector"
          containerClass="ds-scroll-datepicker"
          dropdownMode="scroll"
          scrollableYearDropdown
          yearDropdownItemNumber={30}
          selected={selectedStart}
          onChange={handleStartDate}
          placeholderText="Período Inicial"
          dateFormat="P HH:mm"
          showTimeSelect
          filterDate={startDateFilter}
          filterTime={startDateFilter}
          minDate={OBSERVED_DATA_MIN_DATE}
          maxDate={presentDate}
        />
        <DatePicker
          className="period-selector"
          containerClass="ds-scroll-datepicker"
          dropdownMode="scroll"
          scrollableYearDropdown
          yearDropdownItemNumber={30}
          selected={selectedEnd}
          onChange={handleEndDate}
          placeholderText="Período Final"
          dateFormat="P HH:mm"
          showTimeSelect
          filterDate={endDateFilter}
          filterTime={endDateTimeFilter}
          minDate={OBSERVED_DATA_MIN_DATE}
          maxDate={presentDate}
        />
      </Stack>
    );
  }

  const getSelector = () => {
    if (isHeatmap) return <PeriodSelectors />;
    switch (mode) {
      case 'preset':
        return <PeriodSelectors />;
      case 'date':
        return <DateSelector />;
      default:
        return <FreePeriodSetter />;
    }
  };

  const setViewMode = (next: MeteorologyViewMode) => {
    dispatcher(setViewModeRedux(next));
  };

  return {
    areParamsSelected,
    RegionSelectorOD,
    VariableSelector,
    region: plotArea,
    startDate,
    endDate,
    selectedPeriod,
    getSelector,
    setMode,
    mode,
    anomaly,
    toggleAnomaly,
    obsBase64,
    climatologyBase64,
    anomalyBase64,
    heatmapBase64,
    queryTrigger: queryTriggerMethod,
    imagesAreFetching,
    alerts,
    availableTabs,
    viewMode,
    setViewMode,
    isHeatmap,
    isConsultedHeatmap,
    consultedViewMode,
    isHeatmapDisabled,
    optionsLoading,
    variable,
    availableVariables,
    setSelectedVariable,
    regionOptions,
    setRegion,
    variableLabel,
    viewModeLabel,
    plotAreaLabel,
    periodLabel,
  };
}
